import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, ChevronDown, ChevronRight, ArrowUp, ArrowDown, Calculator } from "lucide-react";
import { TemplateLineItem, TemplateSubLine } from "@/hooks/useEstimateTemplates";
import { QuantityMode, QuantityFormula, applyFormula } from "@/hooks/useEstimates";

const UNITS = ["SF", "LF", "EA", "LS", "HR", "DAY", "TON", "GAL", "BAG", "ROLL", "SQ", "BOX", "BDL"];

interface Props {
  lines: TemplateLineItem[];
  onChange: (lines: TemplateLineItem[]) => void;
}

function emptySubLine(parentUnit: string, sortOrder: number): TemplateSubLine {
  return {
    type: "labor",
    description: "",
    unit: parentUnit,
    quantity_mode: "auto",
    quantity_formula: null,
    sort_order: sortOrder,
  };
}

function emptyLine(): TemplateLineItem {
  return {
    description: "",
    unit: "SF",
    sub_lines: [emptySubLine("SF", 0)],
  };
}

function FormulaPreview({ formula, parentUnit }: { formula: QuantityFormula | null | undefined; parentUnit: string }) {
  if (!formula) return null;
  const exampleQty = 500;
  const result = applyFormula(exampleQty, parentUnit, formula);
  const label = formula.kind === "coverage"
    ? `${exampleQty} ${parentUnit} → ${result} (1 per ${formula.coverage_per_unit ?? "?"} ${formula.source_unit})`
    : `${exampleQty} ${parentUnit} → ${result} (×${formula.multiplier ?? "?"})`;
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
      <Calculator className="h-3 w-3" />
      <span>{label}</span>
    </div>
  );
}

function SubLineEditor({
  sl,
  parentUnit,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  sl: TemplateSubLine;
  parentUnit: string;
  onChange: (updated: TemplateSubLine) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const updateFormula = (patch: Partial<QuantityFormula>) => {
    const current: QuantityFormula = sl.quantity_formula ?? {
      kind: "coverage",
      source: "parent_qty",
      source_unit: parentUnit,
    };
    onChange({ ...sl, quantity_formula: { ...current, ...patch } });
  };

  return (
    <div className="border rounded-md p-3 space-y-3 bg-muted/30">
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-0.5">
          <Button variant="ghost" size="icon" className="h-5 w-5" disabled={!canMoveUp} onClick={onMoveUp}><ArrowUp className="h-3 w-3" /></Button>
          <Button variant="ghost" size="icon" className="h-5 w-5" disabled={!canMoveDown} onClick={onMoveDown}><ArrowDown className="h-3 w-3" /></Button>
        </div>
        <Badge variant="outline" className="text-xs shrink-0">
          {sl.type === "labor" ? "Labor" : "Material"}
        </Badge>
        <span className="text-xs text-muted-foreground">#{sl.sort_order + 1}</span>
        <div className="flex-1" />
        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={sl.type} onValueChange={(v) => onChange({ ...sl, type: v as "labor" | "material" })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="labor">Labor</SelectItem>
              <SelectItem value="material">Material</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Input className="h-8 text-xs" value={sl.description} onChange={(e) => onChange({ ...sl, description: e.target.value })} placeholder="e.g. Grout" />
        </div>
        <div>
          <Label className="text-xs">Unit</Label>
          <Select value={sl.unit} onValueChange={(v) => onChange({ ...sl, unit: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Qty Mode</Label>
          <Select
            value={sl.quantity_mode}
            onValueChange={(v) => {
              const mode = v as QuantityMode;
              const updates: Partial<TemplateSubLine> = { quantity_mode: mode };
              if (mode === "formula" && !sl.quantity_formula) {
                updates.quantity_formula = { kind: "coverage", source: "parent_qty", source_unit: parentUnit };
              }
              if (mode !== "formula") {
                updates.quantity_formula = null;
              }
              onChange({ ...sl, ...updates });
            }}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="formula">Formula</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Default Markup %</Label>
          <Input className="h-8 text-xs" type="number" value={sl.default_markup_pct ?? ""} onChange={(e) => onChange({ ...sl, default_markup_pct: e.target.value ? parseFloat(e.target.value) : undefined })} placeholder={sl.type === "labor" ? "35" : "20"} />
        </div>
      </div>

      {sl.quantity_mode === "formula" && (
        <div className="border rounded p-3 space-y-3 bg-background">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Formula Configuration</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Formula Type</Label>
              <Select value={sl.quantity_formula?.kind ?? "coverage"} onValueChange={(v) => updateFormula({ kind: v as "coverage" | "multiplier" })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coverage">Coverage</SelectItem>
                  <SelectItem value="multiplier">Multiplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Source Unit</Label>
              <Select value={sl.quantity_formula?.source_unit ?? parentUnit} onValueChange={(v) => updateFormula({ source_unit: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(sl.quantity_formula?.kind ?? "coverage") === "coverage" ? (
              <div>
                <Label className="text-xs">Coverage per Unit</Label>
                <Input className="h-8 text-xs" type="number" placeholder="e.g. 200" value={sl.quantity_formula?.coverage_per_unit ?? ""} onChange={(e) => updateFormula({ coverage_per_unit: e.target.value ? parseFloat(e.target.value) : undefined })} />
                <p className="text-[10px] text-muted-foreground mt-0.5">1 unit output per this many source units</p>
              </div>
            ) : (
              <div>
                <Label className="text-xs">Multiplier</Label>
                <Input className="h-8 text-xs" type="number" step="0.01" placeholder="e.g. 1.10" value={sl.quantity_formula?.multiplier ?? ""} onChange={(e) => updateFormula({ multiplier: e.target.value ? parseFloat(e.target.value) : undefined })} />
                <p className="text-[10px] text-muted-foreground mt-0.5">e.g. 1.10 for 10% waste</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Round Mode</Label>
              <Select value={sl.quantity_formula?.round_mode ?? "none"} onValueChange={(v) => updateFormula({ round_mode: v as QuantityFormula["round_mode"] })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="up">Round Up</SelectItem>
                  <SelectItem value="down">Round Down</SelectItem>
                  <SelectItem value="nearest">Round Nearest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Minimum Qty</Label>
              <Input className="h-8 text-xs" type="number" placeholder="e.g. 1" value={sl.quantity_formula?.min_qty ?? ""} onChange={(e) => updateFormula({ min_qty: e.target.value ? parseFloat(e.target.value) : undefined })} />
            </div>
          </div>
          <FormulaPreview formula={sl.quantity_formula} parentUnit={parentUnit} />
        </div>
      )}
    </div>
  );
}

export function TemplateLineEditor({ lines, onChange }: Props) {
  const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set([0]));

  const toggleExpand = (idx: number) => {
    setExpandedLines(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const updateLine = (idx: number, patch: Partial<TemplateLineItem>) => {
    const updated = [...lines];
    updated[idx] = { ...updated[idx], ...patch };
    onChange(updated);
  };

  const removeLine = (idx: number) => {
    if (lines.length <= 1) return;
    onChange(lines.filter((_, i) => i !== idx));
  };

  const addLine = () => {
    const newLines = [...lines, emptyLine()];
    setExpandedLines(prev => new Set([...prev, newLines.length - 1]));
    onChange(newLines);
  };

  const updateSubLine = (lineIdx: number, slIdx: number, updated: TemplateSubLine) => {
    const newSubLines = [...lines[lineIdx].sub_lines];
    newSubLines[slIdx] = updated;
    updateLine(lineIdx, { sub_lines: newSubLines });
  };

  const removeSubLine = (lineIdx: number, slIdx: number) => {
    const newSubLines = lines[lineIdx].sub_lines.filter((_, i) => i !== slIdx)
      .map((sl, i) => ({ ...sl, sort_order: i }));
    updateLine(lineIdx, { sub_lines: newSubLines });
  };

  const addSubLine = (lineIdx: number) => {
    const line = lines[lineIdx];
    const newSl = emptySubLine(line.unit, line.sub_lines.length);
    newSl.type = "material"; // default new sub-lines to material
    updateLine(lineIdx, { sub_lines: [...line.sub_lines, newSl] });
  };

  const moveSubLine = (lineIdx: number, slIdx: number, direction: -1 | 1) => {
    const sls = [...lines[lineIdx].sub_lines];
    const target = slIdx + direction;
    if (target < 0 || target >= sls.length) return;
    [sls[slIdx], sls[target]] = [sls[target], sls[slIdx]];
    updateLine(lineIdx, { sub_lines: sls.map((sl, i) => ({ ...sl, sort_order: i })) });
  };

  return (
    <div className="space-y-4">
      {lines.map((line, lineIdx) => {
        const isExpanded = expandedLines.has(lineIdx);
        return (
          <Card key={lineIdx}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => toggleExpand(lineIdx)}>
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
                <span className="text-sm font-medium text-muted-foreground">Line {lineIdx + 1}</span>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <Input className="h-8 text-sm" placeholder="Description" value={line.description} onChange={(e) => updateLine(lineIdx, { description: e.target.value })} />
                  <Select value={line.unit} onValueChange={(v) => updateLine(lineIdx, { unit: v })}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Badge variant="secondary" className="text-xs shrink-0">{line.sub_lines.length} sub</Badge>
                {lines.length > 1 && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" onClick={() => removeLine(lineIdx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              {isExpanded && (
                <div className="pl-9 space-y-3">
                  <Separator />
                  {line.sub_lines.map((sl, slIdx) => (
                    <SubLineEditor
                      key={slIdx}
                      sl={sl}
                      parentUnit={line.unit}
                      onChange={(updated) => updateSubLine(lineIdx, slIdx, updated)}
                      onRemove={() => removeSubLine(lineIdx, slIdx)}
                      onMoveUp={() => moveSubLine(lineIdx, slIdx, -1)}
                      onMoveDown={() => moveSubLine(lineIdx, slIdx, 1)}
                      canMoveUp={slIdx > 0}
                      canMoveDown={slIdx < line.sub_lines.length - 1}
                    />
                  ))}
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => addSubLine(lineIdx)}>
                    <Plus className="h-3 w-3 mr-1" /> Add Sub-Line
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Button variant="outline" onClick={addLine} className="w-full">
        <Plus className="h-4 w-4 mr-1" /> Add Line
      </Button>
    </div>
  );
}
